// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { DataTableSkeleton, SkeletonText } from '@carbon/react';
import { useTranslation } from 'react-i18next';
import { array, bool, func, object } from 'prop-types';
import { useForm } from 'react-hook-form';
import debounce from 'lodash/debounce';

import OfferEditorComponent from 'Components/Offers/OfferEditor';
import NotFound from 'Components/NotFound/NotFound';
import OrderProductsModal from 'Containers/Order/OrderProductsModal';
import UnavailableProductsConfirmationModal from 'Components/Cart/UnavailableProductsConfirmationModal';
import useAnalytics from 'Hooks/useAnalytics';
import useFetch from 'Hooks/useFetch';
import useNumberUtils from 'Hooks/useNumberUtils';
import useToastNotifications from 'Hooks/useToastNotifications';
import { API_OFFER_PRODUCTS_DETAILS, EMAIL_REGEX } from 'Constants';
import copyToClipboard from 'Utils/copyToClipboard';
import isOfferExpired, {
  clampMarkupPercent,
  getOfferProductErrors,
  getOfferValidUntil,
  getProductPrice,
  getUnitOfferPriceInRon,
  isValidClientEmail,
  OFFER_PRODUCT_STATUS_TRANSLATIONS,
  roundMoney,
  VAT_RATE
} from 'Utils/offers';

const mergeRefreshedProduct = (currentProduct, refreshedProduct, productId) => ({
  ...currentProduct,
  ...refreshedProduct,
  id: refreshedProduct.id || productId,
  quantity: currentProduct.quantity,
  markupPercent: currentProduct.markupPercent,
  unitCustomPrice: currentProduct.unitCustomPrice
});

const getDateInputValue = value => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('-');
};

const replaceDatePart = (value, dateValue) => {
  const date = new Date(value);
  const [year, month, day] = dateValue.split('-').map(Number);
  if (Number.isNaN(date.getTime()) || !year || !month || !day) return value;

  date.setFullYear(year, month - 1, day);
  return date.toISOString();
};

const OfferEditor = memo(({
  offer,
  exchangeRates = [],
  isLoading,
  isSaving,
  isSavedOffer,
  productErrors: savedProductErrors = [],
  updateOffer,
  addProductToOffer,
  updateOfferProduct,
  removeProductFromOffer,
  onRefreshPrices,
  onRenew,
  exportOffer,
  previewAddToCart,
  addOfferToCart,
  onSave,
  onSaved
}) => {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const fetcher = useFetch();
  const toast = useToastNotifications();
  const { formatNumber } = useNumberUtils();
  const [now, setNow] = useState(Date.now());
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [renewLoading, setRenewLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);
  const [cartPreview, setCartPreview] = useState(null);
  const [requestProductErrors, setRequestProductErrors] = useState([]);
  const [exportFormat, setExportFormat] = useState('csv');
  const trackedOfferId = useRef(null);
  const {
    formState: { errors: rhfErrors },
    register,
    reset,
    trigger
  } = useForm({
    defaultValues: {
      name: '',
      clientName: '',
      clientEmail: ''
    },
    mode: 'onChange'
  });
  const maxLength = (value, message) => ({ value, message });
  const validators = {
    name: {
      validate: value => value.trim().length >= 3 || t('offers.offerNameLength'),
      maxLength: maxLength(255, t('offers.offerNameLength'))
    },
    clientName: {
      maxLength: maxLength(255, t('offers.clientNameLength'))
    },
    clientEmail: {
      maxLength: maxLength(255, t('offers.clientEmailLength')),
      pattern: { value: EMAIL_REGEX, message: t('offers.invalidClientEmail') }
    }
  };
  const expiresAt = new Date(offer?.validUntil).getTime();
  const isExpired = isOfferExpired(offer)
    || (Number.isFinite(expiresAt) && expiresAt <= now);
  const currencyId = String(offer?.currencyId || 'ron').toLowerCase();
  const currentExchangeRate = exchangeRates.find(
    rate => rate.currency?.toLowerCase() === currencyId
  )?.rate;
  const storedExchangeRate = Number(offer?.exchangeRate) > 0 ? Number(offer.exchangeRate) : 1;
  const exchangeRate = Number(currentExchangeRate) > 0
    ? Number(currentExchangeRate)
    : storedExchangeRate;
  const priceConversionRate = isSavedOffer ? 1 : exchangeRate;
  const productErrors = useMemo(() => {
    const errorsByProduct = new Map();
    [...savedProductErrors, ...requestProductErrors].forEach(error => {
      if (error?.productId) errorsByProduct.set(String(error.productId), error);
    });
    return [...errorsByProduct.values()];
  }, [requestProductErrors, savedProductErrors]);

  const captureProductErrors = error => {
    const nextProductErrors = getOfferProductErrors(error);
    if (nextProductErrors.length) setRequestProductErrors(nextProductErrors);
    return nextProductErrors;
  };

  const analyticsRef = useRef(analytics);
  analyticsRef.current = analytics;
  const fieldChangeDebouncers = useRef(new Map());

  const trackFieldChange = useMemo(() => (eventName, params, key = eventName) => {
    const debouncers = fieldChangeDebouncers.current;
    if (!debouncers.has(key)) {
      debouncers.set(key, debounce((name, payload) => analyticsRef.current.track(name, payload), 2000));
    }
    debouncers.get(key)(eventName, params);
  }, []);

  useEffect(() => () => {
    fieldChangeDebouncers.current.forEach(fn => fn.flush());
  }, []);

  useEffect(() => {
    if (!offer?.id) return;
    setRequestProductErrors([]);
    reset({
      name: offer.name || '',
      clientName: offer.clientName || '',
      clientEmail: offer.clientEmail || ''
    });
  }, [offer?.id, reset]);

  useEffect(() => {
    if (offer?.id && trackedOfferId.current !== offer.id) {
      trackedOfferId.current = offer.id;
      analytics.track('offer_editor_viewed', {
        offer_id: offer.id,
        offer_status: offer.status,
        product_count: offer.items?.length || 0,
        source: offer.sourceWishlist ? 'wishlist' : 'product_listing'
      });
    }
  }, [analytics, offer]);

  useEffect(() => {
    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      setNow(Date.now());
      return undefined;
    }

    const timeout = setTimeout(() => setNow(Date.now()), (expiresAt - Date.now()) + 50);
    return () => clearTimeout(timeout);
  }, [expiresAt]);

  const products = useMemo(
    () => (offer?.items || []).map(product => {
      const nodPriceInRon = getProductPrice(product);
      const unitOfferPriceInRon = getUnitOfferPriceInRon(product, offer.defaultMarkupPercent);
      const nodPrice = roundMoney(nodPriceInRon / priceConversionRate);
      const unitOfferPrice = roundMoney(unitOfferPriceInRon / priceConversionRate);
      return {
        ...product,
        nodPrice,
        unitOfferPrice,
        unitCustomPriceDisplay: product.unitCustomPrice !== null
          && product.unitCustomPrice !== undefined
          ? roundMoney(Number(product.unitCustomPrice) / priceConversionRate)
          : null,
        lineTotal: roundMoney(unitOfferPrice * Number(product.quantity || 0))
      };
    }),
    [offer, priceConversionRate]
  );

  const totals = useMemo(() => {
    const subtotal = products.reduce((sum, product) => sum + product.lineTotal, 0);
    const calculatedVat = roundMoney(products.reduce((sum, product) => (
      sum + roundMoney(
        product.lineTotal * (Number(product.vatPercent ?? VAT_RATE * 100) / 100)
      )
    ), 0));
    const vat = isSavedOffer ? offer?.totalVat : calculatedVat;
    const numericVat = Number(vat);
    return {
      subtotal: roundMoney(subtotal),
      vat,
      total: roundMoney(subtotal + (Number.isFinite(numericVat) ? numericVat : calculatedVat))
    };
  }, [isSavedOffer, offer?.totalVat, products]);

  if (isLoading) {
    return (
      <div className="grid-container">
        <SkeletonText heading width="30%" />
        <SkeletonText paragraph lineCount={ 2 } width="100%" />
        <DataTableSkeleton
          columnCount={ 9 }
          rowCount={ 3 }
          showHeader={ false }
          showToolbar={ false }
        />
      </div>
    );
  }

  if (!offer) {
    return (
      <NotFound
        title={ t('common.oops') }
        description={ t('offers.offerNotFound') }
        buttonText={ t('offers.backToProducts') }
        url="/products"
      />
    );
  }

  const currencies = exchangeRates.length
    ? exchangeRates.map(rate => ({
      ...rate,
      id: rate.currency.toLowerCase(),
      label: rate.currency === 'RON' ? 'LEI' : rate.currency
    }))
    : [{
      id: currencyId,
      label: currencyId === 'ron' ? 'LEI' : currencyId.toUpperCase(),
      rate: exchangeRate
    }];
  const formatMoney = value => formatNumber(value, {
    currency: currencyId.toUpperCase(),
    maximumFractionDigits: 2
  });

  const handleOfferChange = async event => {
    if (isExpired) return;
    const { name, value } = event.target;
    if (!await trigger(name)) return;
    updateOffer(offer.id, {
      [name]: value
    });
    trackFieldChange('offer_editor_field_changed', {
      offer_id: offer.id,
      field_name: name,
      has_value: value.trim().length > 0,
      value_length: value.length
    }, `offer_editor_field_changed:${ name }`);
  };

  const handleValidUntilChange = event => {
    const minimumValidUntil = getDateInputValue(new Date());
    if (isExpired || !event.target.value || event.target.value < minimumValidUntil) return;
    const validUntil = replaceDatePart(offer.validUntil, event.target.value);
    updateOffer(offer.id, { validUntil });
    trackFieldChange('offer_editor_field_changed', {
      offer_id: offer.id,
      field_name: 'validUntil',
      has_value: true,
      value_length: event.target.value.length
    }, 'offer_editor_field_changed:validUntil');
  };

  const handleProductChange = (product, field, value) => {
    if (isExpired) return;
    const productId = product.productId || product.id;
    if (field === 'quantity') {
      const minQuantity = Math.max(1, Number(product.minQuantity || 1));
      const stockValue = Math.max(0, Number(product.stockValue ?? product.stock) || 0);
      const maxQuantity = Math.max(100, stockValue);
      const quantity = Math.min(
        maxQuantity,
        Math.max(minQuantity, Number(value) || minQuantity)
      );
      updateOfferProduct(offer.id, productId, {
        quantity
      });
      trackFieldChange('offer_editor_product_quantity_changed', {
        offer_id: offer.id,
        product_id: productId,
        quantity
      }, `offer_editor_product_quantity_changed:${ productId }`);
      return;
    }
    let normalizedValue = null;
    if (value !== '') {
      normalizedValue = field === 'markupPercent'
        ? roundMoney(clampMarkupPercent(value))
        : roundMoney(Math.max(0, Number(value)) * priceConversionRate);
    }
    updateOfferProduct(offer.id, productId, { [field]: normalizedValue });
    const productEvent = field === 'markupPercent'
      ? 'offer_editor_product_markup_changed'
      : 'offer_editor_product_custom_price_changed';
    trackFieldChange(
      productEvent,
      {
        offer_id: offer.id,
        product_id: productId,
        has_value: value !== '',
        value: normalizedValue,
        currency_id: currencyId
      },
      `${ productEvent }:${ productId }`
    );
  };

  const handleAddProduct = async product => {
    if (isExpired) return null;
    let result;
    try {
      result = await addProductToOffer(offer.id, product, { allowOutOfStock: true });
    } catch (error) {
      captureProductErrors(error);
      analytics.track('offer_editor_product_add', {
        offer_id: offer.id,
        product_id: product.productId || product.id,
        result: 'failed'
      });
      toast.addNotification({
        type: 'error',
        title: error?.info?.errors?.[0]?.message
          || error?.message
          || t('offers.productAddFailed')
      });
      return null;
    }
    let resultType = 'added';
    if (result?.alreadyExists) {
      resultType = 'already_exists';
    } else if (result?.expired) {
      resultType = 'expired';
    } else if (result?.unavailable) {
      resultType = 'unavailable';
    } else if (result?.outOfStock) {
      resultType = 'out_of_stock';
    } else if (result?.saveFailed) {
      resultType = 'save_failed';
    }
    analytics.track('offer_editor_product_add', {
      offer_id: offer.id,
      product_id: product.productId || product.id,
      result: resultType
    });
    if (result?.saveFailed) {
      // The pending save failed; the container already surfaced that error.
      return null;
    }
    let notificationType = 'success';
    let notificationTitle = t('offers.productAdded', { name: offer.name });
    if (result?.unavailable) {
      notificationType = 'error';
      notificationTitle = t('offers.productUnavailable');
    } else if (result?.outOfStock) {
      notificationType = 'error';
      notificationTitle = t('offers.productOutOfStock');
    } else if (result?.alreadyExists) {
      notificationType = 'info';
      notificationTitle = t('offers.productAlreadyExists');
    }
    toast.addNotification({
      type: notificationType,
      title: notificationTitle
    });
    return result;
  };

  const handleExport = async () => {
    if (!await trigger(['name', 'clientName', 'clientEmail'])) return;
    analytics.track('offer_editor_export_requested', {
      offer_id: offer.id,
      format: exportFormat,
      file_type: exportFormat,
      product_count: products.length
    });
    setExportLoading(true);
    try {
      await exportOffer({
        offerId: offer.id,
        format: exportFormat,
        payload: isSavedOffer
          ? undefined
          : {
            ...offer,
            numberOfProducts: products.length,
            totalWithoutVat: totals.subtotal,
            totalVat: totals.vat,
            totalWithVat: totals.total,
            validUntil: offer.validUntil,
            items: offer.items.map((item, index) => {
              const { ...itemData } = item;
              return {
                ...itemData,
                code: item.code,
                title: item.title,
                unitOfferPrice: products[index].unitOfferPrice,
                totalWithoutVat: products[index].lineTotal,
                totalVat: roundMoney(
                  products[index].lineTotal * (Number(item.vatPercent ?? 21) / 100)
                ),
                totalWithVat: roundMoney(
                  products[index].lineTotal
                  + roundMoney(
                    products[index].lineTotal * (Number(item.vatPercent ?? 21) / 100)
                  )
                )
              };
            })
          }
      });
      toast.addNotification({
        type: 'success',
        title: t('offers.exportRequestSuccess')
      });
      analytics.track('offer_editor_export_succeeded', {
        offer_id: offer.id,
        format: exportFormat,
        file_type: exportFormat
      });
    } catch (error) {
      captureProductErrors(error);
      analytics.track('offer_editor_export_failed', {
        offer_id: offer.id,
        format: exportFormat,
        file_type: exportFormat
      });
      toast.addNotification({
        type: 'error',
        title: error?.info?.errors?.[0]?.message || t('offers.exportRequestFailed')
      });
    } finally {
      setExportLoading(false);
    }
  };

  const handleSave = async () => {
    if (!onSave || isExpired || saveLoading) return;
    if (!await trigger(['name', 'clientName', 'clientEmail'])) return;

    const name = offer.name.trim();
    const clientName = String(offer.clientName || '').trim();
    const clientEmail = String(offer.clientEmail || '').trim();
    const isValidEmail = !clientEmail || isValidClientEmail(clientEmail);

    let validationError = null;
    if (name.length < 3 || name.length > 255) {
      validationError = t('offers.offerNameLength');
    } else if (clientName.length > 255) {
      validationError = t('offers.requiredClientName');
    } else if (clientEmail.length > 255 || !isValidEmail) {
      validationError = t('offers.invalidClientEmail');
    } else if (!offer.items.length || offer.items.length > 500) {
      validationError = t('offers.invalidItemCount');
    } else if (offer.items.some(item => (
      !Number.isInteger(Number(item.quantity)) || Number(item.quantity) <= 0
    ))) {
      validationError = t('offers.invalidItemQuantity');
    }

    if (validationError) {
      toast.addNotification({ type: 'error', title: validationError });
      return;
    }

    const payload = {
      name,
      clientName,
      clientEmail,
      currencyId,
      items: offer.items.map(item => ({
        productId: String(item.productId || item.id),
        quantity: Number(item.quantity),
        ...(item.supplierId && { supplierId: item.supplierId }),
        markupPercent: item.markupPercent === null || item.markupPercent === undefined
          ? null
          : clampMarkupPercent(item.markupPercent),
        unitCustomPrice: item.unitCustomPrice === null || item.unitCustomPrice === undefined
          ? null
          : roundMoney(Number(item.unitCustomPrice) / exchangeRate)
      })),
      defaultMarkupPercent: clampMarkupPercent(offer.defaultMarkupPercent),
      validUntil: offer.validUntil || null
    };

    analytics.track('offer_editor_save_requested', {
      offer_id: offer.id,
      product_count: payload.items.length,
      currency_id: payload.currencyId
    });
    setSaveLoading(true);
    try {
      const response = await onSave(payload);
      analytics.track('offer_editor_save_succeeded', {
        offer_id: offer.id,
        saved_offer_id: response?.offer?.id || response?.id,
        product_count: payload.items.length
      });
      toast.addNotification({
        type: 'success',
        title: t('offers.saveSuccess')
      });
      onSaved?.(response);
    } catch (error) {
      captureProductErrors(error);
      analytics.track('offer_editor_save_failed', {
        offer_id: offer.id,
        product_count: payload.items.length
      });
      toast.addNotification({
        type: 'error',
        title: error?.info?.errors?.[0]?.message || error?.message || t('offers.saveFailed')
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleRefreshPrices = async ({ allowExpired = false } = {}) => {
    if ((!allowExpired && isExpired) || !offer.items.length || pricesLoading) return;

    const productIds = offer.items.map(product => product.productId || product.id);
    const query = productIds
      .map(productId => `productIds[]=${ encodeURIComponent(productId) }`)
      .join('&');
    analytics.track('offer_editor_price_refresh_requested', {
      offer_id: offer.id,
      product_count: productIds.length
    });
    setPricesLoading(true);
    try {
      if (onRefreshPrices) {
        const repricedOffer = await onRefreshPrices(offer.id);
        if (!repricedOffer) {
          // Refresh was aborted (e.g. a pending save failed); error already shown.
          return;
        }
        const refreshedProductCount = repricedOffer?.items?.length || productIds.length;
        const unavailableProductCount = Math.max(productIds.length - refreshedProductCount, 0);
        toast.addNotification({
          type: 'success',
          title: t('offers.refreshPricesSuccess')
        });
        analytics.track('offer_editor_price_refresh_succeeded', {
          offer_id: offer.id,
          refreshed_product_count: refreshedProductCount,
          unavailable_product_count: unavailableProductCount
        });
        setRequestProductErrors([]);
        return;
      }

      const response = await fetcher(`${ API_OFFER_PRODUCTS_DETAILS }?${ query }`);
      const productsResponse = response?.products
        || response?.data?.products
        || response?.data
        || response
        || {};
      const refreshedEntries = Array.isArray(productsResponse)
        ? productsResponse.map(product => [product.productId || product.id, product])
        : Object.entries(productsResponse);
      const repriceProductErrors = refreshedEntries
        .filter(([, product]) => product?.status)
        .map(([responseProductId, product]) => ({
          productId: product.productId || product.id || responseProductId,
          supplierId: product.supplierId ?? null,
          status: product.status
        }));
      setRequestProductErrors(repriceProductErrors);
      const validEntries = refreshedEntries.filter(([, product]) => (
        product
        && typeof product === 'object'
        && !product.message
        && !product.status
      ));
      if (!validEntries.length) {
        throw new Error(t('offers.refreshPricesFailed'));
      }
      const refreshedById = new Map();
      validEntries.forEach(([responseProductId, product]) => {
        refreshedById.set(responseProductId, product);
        if (product.id) refreshedById.set(product.id, product);
        if (product.productId) refreshedById.set(product.productId, product);
      });
      updateOffer(offer.id, currentOffer => ({
        items: currentOffer.items.map(item => {
          const productId = item.productId || item.id;
          const refreshedProduct = refreshedById.get(productId);
          if (!refreshedProduct) {
            return item;
          }
          return mergeRefreshedProduct(item, refreshedProduct, productId);
        })
      }));
      toast.addNotification({
        type: 'success',
        title: t('offers.refreshPricesSuccess')
      });
      analytics.track('offer_editor_price_refresh_succeeded', {
        offer_id: offer.id,
        refreshed_product_count: validEntries.length,
        unavailable_product_count: refreshedEntries.length - validEntries.length
      });
    } catch (error) {
      const nextProductErrors = isSavedOffer
        ? captureProductErrors(error)
        : Object.entries(error?.info?.products || {})
          .filter(([, productError]) => productError?.status)
          .map(([productId, productError]) => ({
            productId,
            status: productError.status
          }));
      if (!isSavedOffer) setRequestProductErrors(nextProductErrors);
      analytics.track('offer_editor_price_refresh_failed', {
        offer_id: offer.id,
        product_count: productIds.length
      });
      toast.addNotification({
        type: 'error',
        title: nextProductErrors.length
          ? t(
            OFFER_PRODUCT_STATUS_TRANSLATIONS[nextProductErrors[0].status]
            || 'offers.cartStatusProductUnavailable'
          )
          : (Array.isArray(error?.info?.errors)
            ? error.info.errors[0]?.message
            : null)
            || error?.message
            || t('offers.refreshPricesFailed')
      });
    } finally {
      setPricesLoading(false);
    }
  };

  const handleRenew = async () => {
    if (renewLoading) return;

    analytics.track('offer_editor_renew_requested', {
      offer_id: offer.id,
      storage: isSavedOffer ? 'saved' : 'private'
    });
    setRenewLoading(true);
    try {
      if (onRenew) {
        const renewedOffer = await onRenew(offer.id);
        if (!renewedOffer) return;
      } else {
        const renewalDate = new Date();
        renewalDate.setHours(0, 0, 0, 0);
        updateOffer(offer.id, { validUntil: getOfferValidUntil(renewalDate) });
        await handleRefreshPrices({ allowExpired: true });
      }
      setNow(Date.now());
      analytics.track('offer_editor_renew_succeeded', {
        offer_id: offer.id,
        storage: isSavedOffer ? 'saved' : 'private'
      });
      toast.addNotification({
        type: 'success',
        title: t('offers.renewSuccess')
      });
    } catch (error) {
      const nextProductErrors = captureProductErrors(error);
      analytics.track('offer_editor_renew_failed', {
        offer_id: offer.id,
        storage: isSavedOffer ? 'saved' : 'private'
      });
      toast.addNotification({
        type: 'error',
        title: nextProductErrors.length
          ? t(
            OFFER_PRODUCT_STATUS_TRANSLATIONS[nextProductErrors[0].status]
            || 'offers.cartStatusProductUnavailable'
          )
          : error?.info?.errors?.[0]?.message
          || error?.message
          || t('offers.renewFailed')
      });
    } finally {
      setRenewLoading(false);
    }
  };

  const addPreviewedProductsToCart = async preview => {
    await addOfferToCart(preview);
    analytics.track('offer_editor_add_to_cart_succeeded', {
      offer_id: offer.id,
      storage: isSavedOffer ? 'saved' : 'private',
      addable_count: preview?.addableCount || 0,
      skipped_count: preview?.skippedCount || 0
    });
    toast.addNotification({
      type: 'success',
      title: t(preview?.addableCount ? 'offers.cartAddSuccess' : 'offers.cartNoProductsAdded')
    });
  };

  const handleAddToCart = async () => {
    if (!previewAddToCart || !addOfferToCart || cartLoading || !offer.items.length) return;

    analytics.track('offer_editor_add_to_cart_requested', {
      offer_id: offer.id,
      storage: isSavedOffer ? 'saved' : 'private',
      product_count: offer.items.length
    });
    setCartLoading(true);
    try {
      const preview = await previewAddToCart(offer);
      if (!preview) return;
      const skippedProducts = (preview.items || []).filter(item => !item.isAddable);
      if (skippedProducts.length) {
        setCartPreview({ ...preview, skippedProducts });
        analytics.track('offer_editor_add_to_cart_confirmation_opened', {
          offer_id: offer.id,
          skipped_count: skippedProducts.length
        });
      } else {
        await addPreviewedProductsToCart(preview);
      }
    } catch (error) {
      captureProductErrors(error);
      analytics.track('offer_editor_add_to_cart_failed', { offer_id: offer.id });
      toast.addNotification({
        type: 'error',
        title: error?.info?.errors?.[0]?.message
          || error?.message
          || t('offers.cartAddFailed')
      });
    } finally {
      setCartLoading(false);
    }
  };

  const handleConfirmAddToCart = async () => {
    try {
      await addPreviewedProductsToCart(cartPreview);
      setCartPreview(null);
    } catch (error) {
      captureProductErrors(error);
      analytics.track('offer_editor_add_to_cart_failed', { offer_id: offer.id });
      toast.addNotification({
        type: 'error',
        title: error?.info?.errors?.[0]?.message
          || error?.message
          || t('offers.cartAddFailed')
      });
      throw error;
    }
  };

  const excludedProductIds = offer.items.map(product => product.productId || product.id);

  return (
    <>
      <OfferEditorComponent
        offer={ offer }
        products={ products }
        currencies={ currencies }
        exportLoading={ exportLoading }
        saveLoading={ saveLoading }
        isSaving={ isSaving }
        pricesLoading={ pricesLoading }
        renewLoading={ renewLoading }
        cartLoading={ cartLoading }
        exportFormat={ exportFormat }
        isExpired={ isExpired }
        isSavedOffer={ isSavedOffer }
        productErrors={ productErrors }
        exportFormats={ [
          { id: 'csv', label: 'CSV' },
          { id: 'docx', label: 'Word' },
          { id: 'pdf', label: 'PDF' }
        ] }
        validUntil={ getDateInputValue(offer.validUntil) }
        minimumValidUntil={ getDateInputValue(new Date()) }
        totals={ totals }
        formatMoney={ formatMoney }
        validators={ validators }
        register={ register }
        rhfErrors={ rhfErrors }
        onOfferChange={ handleOfferChange }
        onValidUntilChange={ handleValidUntilChange }
        onCurrencyChange={ ({ selectedItem }) => {
          if (!isExpired) {
            analytics.track('offer_editor_currency_changed', {
              offer_id: offer.id,
              previous_currency_id: currencyId,
              currency_id: selectedItem.id
            });
            updateOffer(offer.id, {
              currencyId: selectedItem.id,
              exchangeRate: Number(selectedItem.rate) || 1
            });
          }
        } }
        onGlobalMarkupChange={ event => {
          if (isExpired) return;
          const previousGlobalMarkup = clampMarkupPercent(offer.defaultMarkupPercent);
          const globalMarkup = clampMarkupPercent(event.target.value);
          const affectedProductCount = offer.items.filter(product => (
            product.markupPercent === null
            || product.markupPercent === undefined
            || clampMarkupPercent(product.markupPercent) === previousGlobalMarkup
          )).length;
          trackFieldChange('offer_editor_global_markup_changed', {
            offer_id: offer.id,
            markup_percent: globalMarkup,
            affected_product_count: affectedProductCount
          });
          updateOffer(offer.id, currentOffer => ({
            defaultMarkupPercent: globalMarkup,
            items: currentOffer.items.map(product => (
              product.markupPercent !== null
              && product.markupPercent !== undefined
              && clampMarkupPercent(product.markupPercent) === previousGlobalMarkup
                ? { ...product, markupPercent: globalMarkup }
                : product
            ))
          }));
        } }
        onOpenProductModal={ () => {
          if (!isExpired) {
            analytics.track('offer_editor_add_product_modal_opened', {
              offer_id: offer.id,
              product_count: offer.items.length
            });
            setProductModalOpen(true);
          }
        } }
        onRefreshPrices={ handleRefreshPrices }
        onRenew={ handleRenew }
        onProductChange={ handleProductChange }
        onRemoveProduct={ product => {
          const productId = product.productId || product.id;
          const hasProductError = productErrors.some(
            error => String(error.productId).trim().toLowerCase()
              === String(productId).trim().toLowerCase()
          );
          if (isExpired && (!isSavedOffer || !hasProductError)) return;

          analytics.track('offer_editor_product_removed', {
            offer_id: offer.id,
            product_id: productId
          });
          removeProductFromOffer(offer.id, productId);
          setRequestProductErrors(currentErrors => currentErrors.filter(
            error => String(error.productId) !== String(productId)
          ));
        } }
        onCopySku={ async code => {
          if (await copyToClipboard(code || '')) {
            analytics.track('offer_editor_sku_copied', {
              offer_id: offer.id,
              product_sku: code
            });
            toast.addNotification({
              type: 'success',
              title: t('products.copyCodeSuccess')
            });
          }
        } }
        onExportFormatChange={ ({ selectedItem }) => {
          analytics.track('offer_editor_export_format_changed', {
            offer_id: offer.id,
            format: selectedItem.id
          });
          setExportFormat(selectedItem.id);
        } }
        onSave={ handleSave }
        onAddToCart={ handleAddToCart }
        onExport={ handleExport }
      />
      <UnavailableProductsConfirmationModal
        open={ !!cartPreview }
        products={ cartPreview?.skippedProducts || [] }
        onConfirm={ handleConfirmAddToCart }
        onCancel={ () => {
          analytics.track('offer_editor_add_to_cart_confirmation_cancelled', {
            offer_id: offer.id
          });
          setCartPreview(null);
        } }
      />
      <OrderProductsModal
        open={ productModalOpen }
        onClose={ () => {
          analytics.track('offer_editor_add_product_modal_closed', { offer_id: offer.id });
          setProductModalOpen(false);
        } }
        onAddProduct={ async product => {
          const result = await handleAddProduct(product);
          if (result) {
            setProductModalOpen(false);
          }
          return result;
        } }
        title={ t('offers.addProductsToOffer') }
        addButtonLabel={ t('offers.addProduct') }
        trackingLocation="offer_products_modal"
        searchAnalyticsEvent="offer_editor_add_product_search"
        buttonAnalyticsEvent="offer_editor_add_product_clicked"
        requiresAddToCartPermission={ false }
        excludedProductIds={ excludedProductIds }
        allowPriceRequestProducts
        allowOutOfStock
        includeSearchFilters={ false }
      />
    </>
  );
});

OfferEditor.displayName = 'OfferEditorContainer';

OfferEditor.propTypes = {
  offer: object,
  exchangeRates: array,
  isLoading: bool,
  isSaving: bool,
  isSavedOffer: bool,
  productErrors: array,
  updateOffer: func,
  addProductToOffer: func,
  updateOfferProduct: func,
  removeProductFromOffer: func,
  onRefreshPrices: func,
  onRenew: func,
  exportOffer: func,
  previewAddToCart: func,
  addOfferToCart: func,
  onSave: func,
  onSaved: func
};

export default OfferEditor;
